import { SDKModel } from '../core/model.js';
import { TenantError, TenantModel } from '../index.js';
export class AuthError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'AuthError';
    }
}
export var TokenCustomClaimKeysEnum;
(function (TokenCustomClaimKeysEnum) {
    TokenCustomClaimKeysEnum["ORG_DISPLAY_NAME"] = "https://auth.sitecorecloud.io/claims/org_display_name";
    TokenCustomClaimKeysEnum["ORG_ID"] = "https://auth.sitecorecloud.io/claims/org_id";
    TokenCustomClaimKeysEnum["ORG_NAME"] = "https://auth.sitecorecloud.io/claims/org_name";
    TokenCustomClaimKeysEnum["ROLES"] = "https://auth.sitecorecloud.io/claims/roles";
    TokenCustomClaimKeysEnum["ORG_TYPE"] = "https://auth.sitecorecloud.io/claims/org_type";
})(TokenCustomClaimKeysEnum || (TokenCustomClaimKeysEnum = {}));
export const TokenCustomClaimOrgAccountIdKey = 'https://auth.sitecorecloud.io/claims/org_account_id';
export function getAuthDefaults(sdk) {
    return {
        isAuthenticated: false,
        isLoading: false,
        user: {
            sub: 'something'
        },
        loginWithRedirect: () => { },
        logout: () => { }
    };
}
export class AuthModel extends SDKModel {
    getDefaults(sdk) {
        return getAuthDefaults(sdk);
    }
    isExternal() {
        return !this.handleRedirectCallback && !!this.getAccessTokenSilently;
    }
    get logout() {
        return this._logout;
    }
    set logout(callback) {
        this._logout = (options) => {
            AuthModel.setLocalStorageContext(null);
            callback(options);
        };
    }
    async retrieveClaims() {
        this.claims = this.getIdTokenClaims ? (await this.getIdTokenClaims()) : null;
    }
    /** Re-authenticate the user. This method is safe to call multiple times, it will only initiate authentication
     * if necessary: If user is not yet logged in, or the user logged in to wrong organization */
    async login(query) {
        if (query === null || query === void 0 ? void 0 : query.error) {
            throw new Error(query.error_description);
        }
        if (!this.isLoading && this.loginWithRedirect) {
            const queryContext = this.getContext(query);
            const requestedOrganizationId = queryContext.organization;
            const lastOrganizationId = this.isAuthenticated
                ? this.user['https://auth.sitecorecloud.io/claims/org_id']
                : AuthModel.getContextFromLocalStorage().organization;
            if (
            // Need to log in?
            !this.isAuthenticated ||
                // Need to switch organizations?
                (requestedOrganizationId && requestedOrganizationId != lastOrganizationId)) {
                AuthModel.setLocalStorageContext(null);
                await this.loginWithRedirect({
                    ...AuthModel.formatContextForAuth0(queryContext),
                    appState: { returnTo: location.pathname }
                });
                return true;
            }
        }
        return false;
    }
    getUser() {
        var _a;
        if (!this.user)
            return {};
        return {
            id: (_a = this.user.sub) === null || _a === void 0 ? void 0 : _a.replace('auth0|', ''),
            name: this.user.name,
            givenName: this.user.given_name,
            familyName: this.user.family_name,
            email: this.user.email,
            picture: this.user.picture,
            nickname: this.user.nickname
        };
    }
    getGainsightUserData() {
        var _a;
        if (!this.user)
            return null;
        const org_account_id = (_a = this.claims) === null || _a === void 0 ? void 0 : _a[TokenCustomClaimOrgAccountIdKey];
        return {
            userFields: {
                id: this.user.email,
                email: this.user.email,
                firstName: this.user.given_name,
                lastName: this.user.family_name
            },
            accountFields: {
                id: org_account_id, // Salesforce Account ID
                sfdcId: org_account_id
            }
        };
    }
    getGainsightGlobalContext() {
        var _a, _b, _c, _d;
        const orgRoles = (_a = this.claims) === null || _a === void 0 ? void 0 : _a[TokenCustomClaimKeysEnum.ROLES].filter((role) => role.includes('Organization')).map((orgRole) => orgRole.split('\\')[1]);
        return {
            'Organization DisplayName': (_b = this.claims) === null || _b === void 0 ? void 0 : _b[TokenCustomClaimKeysEnum.ORG_DISPLAY_NAME],
            OrganizationID: (_c = this.claims) === null || _c === void 0 ? void 0 : _c[TokenCustomClaimKeysEnum.ORG_ID],
            'Organization Role': orgRoles.join(', '),
            'Organization Type': ((_d = this.claims) === null || _d === void 0 ? void 0 : _d[TokenCustomClaimKeysEnum.ORG_TYPE]) || ''
        };
    }
    getDefaultContext() {
        return {};
    }
    getContext(query) {
        var _a;
        return {
            ...this.getDefaultContext(),
            ...AuthModel.getContextFromLocalStorage(),
            ...(_a = this.tenant) === null || _a === void 0 ? void 0 : _a.getContext(),
            ...AuthModel.getContextFromUser(this.user),
            ...AuthModel.getContextFromQuery(query)
        };
    }
    initPreflight(query, audience) {
        if (!this.isAuthenticated)
            return;
        const { sdk, getAccessTokenSilently } = this;
        const context = AuthModel.formatContextForAuth0(this.getContext(query));
        // sdk first, then query, then localstorage
        sdk.preflight = async function (url, options) {
            this.accessToken = await getAccessTokenSilently({
                audience: audience,
                ...context
            });
            return options;
        };
    }
    /**
     * - Fetch tenant by tenantName (query or localstorage)
     * - If tenant has access to project/lib, use this tenant
     * - Else fetch all tenants for the user, search if there is a tenant that has access to project/lib and use this tenant
     * - If the user has no tenants at all, throw corresponding error
     * - If user has tenants but none has access to project/lib, throw corresponding error
     */
    async findTenant(query, systemId, libraryId) {
        const { tenantName: name } = this.getContext(query);
        return this.sdk.tenants.search({ name, systemId }).then(async (tenants) => {
            const foundTenants = tenants.length ? tenants : await this.sdk.tenants.search({ systemId });
            if (!foundTenants.length)
                throw new TenantError('User does not have access to any components libraries');
            /** Find tenant that has access to project or that has access to a project (for non-xm-cloud-libraries) */
            const activeTenant = foundTenants.find((tenant) => TenantModel.generateProjectIdFromTenant(tenant) === libraryId) ||
                foundTenants.find((tenant) => TenantModel.generateProjectIdFromTenant(tenant));
            AuthModel.setLocalStorageContext({
                tenantName: activeTenant.name,
                organization: activeTenant.organizationId,
                systemId: activeTenant.systemId
            });
            return activeTenant;
        });
    }
    static setLocalStorageContext(context) {
        localStorage.setItem('feaas:auth', JSON.stringify(context || '{}'));
    }
    static getContextFromQuery(query) {
        const context = {};
        if (query.organization) {
            context.organization = query.organization;
            context.tenantName = query.tenantName || undefined;
        }
        if (query.systemId)
            context.systemId = query.systemId;
        return context;
    }
    static getContextFromUser(user) {
        const context = {};
        if (user === null || user === void 0 ? void 0 : user['https://auth.sitecorecloud.io/claims/tenant_name'])
            context.tenantName = user['https://auth.sitecorecloud.io/claims/tenant_name'];
        if (user === null || user === void 0 ? void 0 : user['https://auth.sitecorecloud.io/claims/org_id'])
            context.organization = user['https://auth.sitecorecloud.io/claims/org_id'];
        return context;
    }
    static getContextFromLocalStorage() {
        const items = JSON.parse(localStorage.getItem('feaas:auth') || '{}');
        const context = {};
        if (items.tenantName)
            context.tenantName = items.tenantName;
        if (items.organization)
            context.organization = items.organization;
        if (items.systemId)
            context.systemId = items.systemId;
        return context;
    }
    static formatContextForAuth0(context) {
        return (({ tenantName: tenant_name, organization: organization_id, systemId: system_id }) => ({
            tenant_name,
            organization_id,
            system_id
        }))(context);
    }
}
//# sourceMappingURL=auth.js.map